import React, {useEffect, useState} from "react";
import {
    Box,
    Button,
    Typography, Chip, LinearProgress, Alert
} from "@mui/material";
import {Cancel, SwapHoriz, Sync, CreditCardOutlined} from "@mui/icons-material";
import ChangePlan from "./ChangePlan";
import {subscriptionServices} from "../../../service/subscriptionService";
import Spinner from "../../spinner/Spinner";
import CancelSubscription from "./CancelSubscription";
import {SubscriptionCard, SubscriptionSectionTitle, subscriptionInset} from "./SubscriptionDesign";

function SubscriptionDetails() {
    const [openChangePlan, setOpenChangePlan] = useState(false);
    const [openCancelSubscription, setOpenCancelSubscription] = useState(false);
    const [loading, setLoading] = useState(true);
    const [errorMsg, setErrorMsg] = useState("");
    const [details, setDetails] = useState(null);

    const handleCloseChangePlan = () => {
        setOpenChangePlan(false);
        setDetails(null);
    };

    const handleClickOpenChangePlan = () => {
        setOpenChangePlan(true);
    };

    const handleCloseCancelSubscription = () => {
        setOpenCancelSubscription(false);
        setDetails(null);
    };

    const handleClickOpenCancelSubscription = () => {
        setOpenCancelSubscription(true);
    };

    const remainSubscription = async () => {
        setLoading(true);
        setErrorMsg("");
        await subscriptionServices.remain().then(() => {
            setDetails(null);
        }).catch((e) => {
            setErrorMsg(e?.response?.data?.error);
            setLoading(false);
        });
    };

    useEffect(() => {
        async function fetchData() {
            setLoading(true);
            setErrorMsg("");
            await subscriptionServices.fetchSubscription("true", "false", "true", "false", "true").then((result) => {
                setDetails(result);
            }).catch((error) => {
                setErrorMsg(error?.response?.data?.error);
            }).finally(() => {
                setLoading(false);
            });
        }

        if (details === null) {
            fetchData();
        }
    }, [details]);

    const getOverageEvents = (max, usedEvents) => {
        if (usedEvents > max) {
            return (usedEvents - max).toLocaleString();
        }
        return 0;
    };

    const getOverageCost = (max, usedEvents, planName, overagePrice) => {
        let overageEvents = 0;
        if (usedEvents > max) {
            overageEvents = usedEvents - max;
        }

        let cost = overageEvents * (overagePrice / 1000);
        return "€" + cost.toFixed(2).toLocaleString() + " (€ " + overagePrice + " per 1000 events)";
    };

    const getMonthlyPayment = () => {
        return details?.nextPayment?.totalAmount == null ? "—" : "€" + details.nextPayment.totalAmount.toFixed(2);
    };

    const limitRows = [
        ['Users', details?.limits?.maxUsers], ['Cost sync integrations', details?.limits?.maxCostSyncIntegrations],
        ['Revenue sync integrations', details?.limits?.maxRevenueSyncIntegrations], ['Server integrations', details?.limits?.maxServerIntegrations],
        ['Custom domains', details?.limits?.maxCustomDomains], ['Entries', details?.limits?.maxEntries],
        ['Website domains', details?.limits?.maxWebsiteDomains], ['Website S3 buckets', details?.limits?.maxWebsiteBuckets],
        ['Website deploys', details?.limits?.maxWebsiteBuckets], ['Custom columns', details?.limits?.maxCustomColumns]
    ];
    const usedEvents = details?.usedEvents || 0;
    const includedEvents = details?.limits?.maxEvents || 0;
    const usage = includedEvents ? Math.min(100, usedEvents / includedEvents * 100) : 0;

    return <SubscriptionCard sx={{minHeight: 240}}>
        <SubscriptionSectionTitle icon={CreditCardOutlined} title="Your plan"/>
        {loading && <Spinner overComponentBox size={32} noBackShadow/>}
        {errorMsg && <Alert variant="filled" severity="error" sx={{mb: 2}}>{errorMsg}</Alert>}
        {!loading && details && <>
            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1}}>
                <Typography sx={{fontSize: 22, fontWeight: 700}}>{details.plan?.name}</Typography>
                <Chip size="small" label={details.cancelSubscriptionAt ? 'Cancellation scheduled' : 'Active'}
                      color={details.cancelSubscriptionAt ? 'warning' : 'primary'} variant="outlined"
                      sx={{borderRadius: '6px'}}/>
            </Box>
            <Typography sx={{fontSize: 36, fontWeight: 700, letterSpacing: '-1px', mb: 2}}>
                €{(details.plan?.price / 100).toFixed(2)} <Box component="span"
                                                               sx={{fontSize: 13, fontWeight: 400, color: 'text.secondary', letterSpacing: 0}}>/
                month</Box>
            </Typography>
            {details.cancelSubscriptionAt &&
                <Alert severity="warning" sx={{mb: 2}}>Subscription ends on {details.cancelSubscriptionAt}.</Alert>}
            {details.nextIssuedPlanName !== details.plan?.name &&
                <Alert severity="info" sx={{mb: 2}}>Your plan change is scheduled
                    for {details.plan?.currentPeriodEnd}.</Alert>}
            <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3}}>
                {!details.cancelSubscriptionAt ? <>
                    {details.nextIssuedPlanName === details.plan?.name
                        ? <Button variant="contained" startIcon={<SwapHoriz/>} onClick={handleClickOpenChangePlan}>Change
                            plan</Button>
                        : <Button variant="contained" startIcon={<Sync/>} onClick={remainSubscription}>Keep current
                            plan</Button>}
                    <Button color="error" startIcon={<Cancel/>} onClick={handleClickOpenCancelSubscription}>Cancel
                        subscription</Button>
                </> : <Button variant="contained" startIcon={<Sync/>} onClick={remainSubscription}>Reactivate
                    subscription</Button>}
            </Box>
            <Box sx={{display: 'grid', gap: 1.5, mb: 3}}>
                <DetailRow label="Current billing period"
                           value={`${details.plan?.currentPeriodStart} – ${details.plan?.currentPeriodEnd}`}/>
                {!details.cancelSubscriptionAt &&
                    <DetailRow label="Next issued plan" value={details.nextIssuedPlanName}/>}
                {details.nextIssuedPlanName === details.plan?.name &&
                    <DetailRow label="Next payment amount" value={getMonthlyPayment()}/>}
            </Box>
            <Box sx={{...subscriptionInset, mb: 3}}>
                <Box sx={{display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 1, mb: 1.5}}>
                    <Typography sx={{fontSize: 14, fontWeight: 600}}>Monthly events</Typography>
                    <Typography
                        sx={{fontSize: 13, color: 'text.secondary'}}>{usedEvents.toLocaleString()} / {includedEvents.toLocaleString()}</Typography>
                </Box>
                <LinearProgress variant="determinate" value={usage} sx={{height: 6, borderRadius: '6px', mb: 2}}/>
                <Box sx={{display: 'grid', gap: 1}}>
                    <DetailRow label="Overage events" value={getOverageEvents(includedEvents, usedEvents)}/>
                    <DetailRow label="Overage cost"
                               value={getOverageCost(includedEvents, usedEvents, details.plan?.name, details.plan?.overagePrice)}/>
                </Box>
            </Box>
            <Typography sx={{fontSize: 14, fontWeight: 600, mb: 2}}>Included limits</Typography>
            <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))'}, gap: 2}}>
                {limitRows.map(([label, value]) => <Box key={label}>
                    <Typography sx={{fontSize: 12, color: 'text.secondary', mb: 0.5}}>{label}</Typography>
                    <Typography sx={{fontSize: 14, fontWeight: 600}}>{value?.toLocaleString()}</Typography>
                </Box>)}
            </Box>
        </>}
        {openChangePlan && <ChangePlan open onClose={handleCloseChangePlan}/>}
        {openCancelSubscription && <CancelSubscription open onClose={handleCloseCancelSubscription}/>}
    </SubscriptionCard>;
}

function DetailRow({label, value}) {
    return <Box
        sx={{display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 1, alignItems: 'baseline'}}>
        <Typography sx={{fontSize: 13, color: 'text.secondary'}}>{label}</Typography>
        <Typography sx={{fontSize: 13, fontWeight: 500, overflowWrap: 'anywhere'}}>{value}</Typography>
    </Box>;
}

export default SubscriptionDetails;